import {
  IButtonStyles,
  IChoiceGroupStyles,
  IComboBoxStyles,
  IDialogContentProps,
  IDialogFooterStyles,
  IDialogStyles,
  IDropdownStyles,
  ITextFieldStyles,
  mergeStyleSets
} from "@fluentui/react";

export const playgroundStyles = {
  buttonStyles: {
    root: {
      background: "var(--colorBrandBackgroundSelected)", // background color
      borderRadius: 10
    },
    rootHovered: {
      background: "var(--colorBrandBackgroundHover)" // hover background color
    },
    rootPressed: {
      background: "var(--colorBrandBackgroundPressed)" // pressed background color
    },
    rootDisabled: {
      background: "var(--colorNeutralBackgroundDisabled)" // disabled background color
    }
  } as IButtonStyles,
  defaultButtonStyles: {
    root: {
      borderRadius: 10
    }
  },
  textFieldStyles: {
    fieldGroup: {
      backgroundColor: "#f3f6fa",
      borderRadius: "12px",
      border: "1px solid #e1e4ea",
      "&:hover": {
        borderColor: "var(--colorBrandBackgroundSelected)"
      },
      "&::after": {
        borderColor: "var(--colorBrandBackgroundSelected)",
        borderRadius: "12px"
      }
    },
    field: {
      backgroundColor: "#f3f6fa",
      border: "1px solid #e1e4ea",
      borderRadius: "12px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.03)"
    }
  } as Partial<ITextFieldStyles>,
  dropdownStyles: {
    dropdown: {
      backgroundColor: "#f3f6fa",
      border: "1px solid #e1e4ea",
      borderRadius: "12px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
      "&:hover": {
        border: "1px solid var(--colorBrandBackgroundSelected)",
        "& .ms-Dropdown-title": {
          border: "0 solid var(--colorBrandBackgroundSelected) !important"
        }
      },
      "&::after": {
        borderColor: "var(--colorBrandBackgroundSelected) !important",
        borderRadius: "12px !important"
      }
    },
    title: {
      backgroundColor: "#f3f6fa",
      borderColor: " #e1e4ea",
      borderRadius: "12px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
      "&:hover": {
        borderColor: "var(--colorBrandBackgroundSelected)"
      },
      "&::after": {
        borderColor: "var(--colorBrandBackgroundSelected)",
        borderRadius: "12px"
      }
    }
  } as Partial<IDropdownStyles>,
  comboBoxStyles: {
    root: {
      backgroundColor: "#f3f6fa",
      border: "1px solid #e1e4ea",
      borderRadius: "12px !important",
      boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
      "&:hover": {
        borderColor: "var(--colorBrandBackgroundSelected)",
        "&::after": {
          borderColor: "#e1e4ea !important",
          borderRadius: "12px"
        }
      },
      "& button.ms-ComboBox-CaretDown-button": {
        backgroundColor: "transparent !important"
      }
    },
    input: {
      backgroundColor: "#f3f6fa"
    }
  } as Partial<IComboBoxStyles>,
  readonlyTextFieldStyles: {
    fieldGroup: {
      backgroundColor: "#f5f5f5",
      border: "1px solid #e1e4ea",
      borderRadius: "12px",
      "&:hover": {
        borderColor: "var(--colorBrandBackgroundSelected)"
      },
      "&::after": {
        borderColor: "var(--colorBrandBackgroundSelected)",
        borderRadius: "12px"
      }
    },
    field: {
      cursor: "default"
    }
  } as Partial<ITextFieldStyles>,
  choiceGroupStyles: {
    label: { display: "inline" },
    flexContainer: {
      display: "inline-flex",
      flexDirection: "row",
      alignItems: "center",
      marginLeft: "12px"
    },
    root: {
      selectors: {
        ".ms-ChoiceFieldLabel": {
          fontWeight: "700 !important"
        }
      }
    }
  } as IChoiceGroupStyles,
  dialogStyles: {
    main: {
      height: "600px",
      display: "flex",
      flexDirection: "column",
      padding: 0
    }
  } as Partial<IDialogStyles>,
  dialogContentStyles: {
    content: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden",
      padding: 0
    },
    inner: {
      padding: 0,
      display: "flex",
      flexDirection: "column",
      height: "100%"
    },
    innerContent: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden"
    }
  } as IDialogContentProps,
  dialogFooterStyles: {
    actions: {
      position: "absolute",
      bottom: 0,
      right: 0,
      backgroundColor: "white",
      padding: "12px 24px"
    }
  } as Partial<IDialogFooterStyles>
};

export const playgroundClassNames = mergeStyleSets({
  mainContainerStyle: {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box"
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "2fr 3fr",
    gap: 16,
    gridTemplateAreas: `
      "userInputsLeft contentsRight"
      "actionButtons actionButtons"
    `,
    marginBottom: "24px"
  },
  leftContainerStyle: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    gridArea: "userInputsLeft"
  },
  rightContainerStyle: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    maxHeight: "900px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    gridArea: "contentsRight",
    padding: "16px"
  },
  resultsContainerStyle: {
    padding: "24px",
    backgroundColor: "#f3f6fa",
    border: "1px solid #e1e4ea",
    borderRadius: "12px",
    minHeight: 600,
    maxHeight: 750,
    overflowY: "auto",
    position: "relative",
    fontSize: "1rem",
    color: "#222",
    boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
    height: "calc(100% - 48px)"
  },
  textFieldContainerStyle: {
    position: "relative",
    marginBottom: "0px"
  },
  copyIconStyle: {
    position: "absolute",
    top: "35px",
    right: "8px",
    cursor: "pointer",
    zIndex: 1,
    color: "#0078d4",
    background: "#f3f6fa",
    borderRadius: "50%",
    padding: 0,
    transition: "background 0.2s",
    ":hover": {
      background: "#e1e4ea"
    }
  },
  iconButton: {
    marginTop: "0px"
  },
  spanStyle: {
    fontStyle: "italic",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#888"
  },
  buttonRow: {
    display: "flex",
    gap: "16px",
    gridArea: "actionButtons",
    gridColumn: "1 / span 2",
    justifyContent: "space-between",
    marginBottom: "0px"
  },
  flexRowWithGap: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    overflowY: "auto",
    flex: 1,
    padding: "16px 24px",
    paddingBottom: "120px",
    minHeight: 0
  },
  scaledChoiceFieldStyle: {
    transform: "scale(0.8)",
    transformOrigin: "top left"
  }
});
